import type { DropdownOption } from "../../redistribution/models/DropdownOption.ts";
import type {
  AssociatedIntermediateOrder,
  BulkOrderCreationRow,
} from "./bulkOrderCreationMock.ts";
import { createSuggestedOrdersDemoRows } from "./suggestedOrdersDemo.ts";

export const DEMO_SHIPPING_SCENARIO_ID = "bulk-order-three-ios-v1";

export function createAlgorithmDemoRows() {
  return createSuggestedOrdersDemoRows(
    () => 0.25,
    (index) => 6 + (index % 3) * 2
  );
}

const DEMO_SHIPPING_ORDERS = [
  { id: "IO-10482", warehouse: "Demo North", warehouseNumber: 901 },
  { id: "IO-10483", warehouse: "Demo Central", warehouseNumber: 902 },
  { id: "IO-10484", warehouse: "Demo South", warehouseNumber: 903 },
] as const;

export const DEMO_ALGORITHM_WAREHOUSES: DropdownOption[] =
  DEMO_SHIPPING_ORDERS.map((order) => ({
    label: order.warehouse,
    value: `DM_${String(order.warehouseNumber)}`,
    metadata: { division: "DM", warehouseID: order.warehouseNumber },
  }));

export function resolveDemoIntermediateOrderId(
  identifier: string
): string | undefined {
  const normalized = identifier
    .trim()
    .toUpperCase()
    .replace(/^(IO-|IO|IPO-?|I-?)/, "");
  return DEMO_SHIPPING_ORDERS.find((order) => order.id.slice(3) === normalized)
    ?.id;
}

export function isDemoAlgorithmIdentifier(identifier: string): boolean {
  return (
    /^(?:Z-?|BO-?)77204$/i.test(identifier.trim()) ||
    resolveDemoIntermediateOrderId(identifier) !== undefined
  );
}

export function splitDemoQuantity(quantity: number): number[] {
  if (!Number.isSafeInteger(quantity) || quantity < 0) {
    throw new TypeError(
      "Demo order quantities must be nonnegative whole numbers."
    );
  }
  const north = Math.floor(quantity * 0.6);
  const central = Math.floor(quantity * 0.3);
  return [north, central, quantity - north - central];
}

export function demoAssociatedOrders(
  rows: BulkOrderCreationRow[]
): AssociatedIntermediateOrder[] {
  const quantities = rows.map((row) => splitDemoQuantity(row.requestedUnits));
  return DEMO_SHIPPING_ORDERS.map((order, index) => ({
    id: order.id,
    warehouse: order.warehouse,
    unitTotal: quantities.reduce(
      (total, split) => total + (split[index] ?? 0),
      0
    ),
  }));
}

export function demoRowsForIntermediateOrder(
  rows: BulkOrderCreationRow[],
  intermediateOrderId: string
): BulkOrderCreationRow[] {
  const index = DEMO_SHIPPING_ORDERS.findIndex(
    (order) => order.id === intermediateOrderId.trim().toUpperCase()
  );
  const order = DEMO_SHIPPING_ORDERS[index];
  if (!order)
    throw new Error(`Unknown demo Intermediate Order: ${intermediateOrderId}.`);
  return rows
    .map((row) => {
      const quantity = splitDemoQuantity(row.requestedUnits)[index] ?? 0;
      return {
        ...row,
        division: "DM",
        requestedUnits: quantity,
        cubicFeet:
          row.requestedUnits > 0
            ? (row.cubicFeet / row.requestedUnits) * quantity
            : 0,
        shipToWarehouse: order.warehouse,
        warehouseNumber: order.warehouseNumber,
      };
    })
    .filter((row) => row.requestedUnits > 0);
}
